import { createPatchData } from 'features/fde/helpers/rolodex/createPatchData'
import {
  FieldValue,
  FieldValues,
  FieldValueSchema,
  FieldValuesSchema,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import { FormLayoutItem } from 'features/libra/sections/formLayout'
import { SavePartialField, Section } from 'features/libra/sections/sectionTypes'
import {
  applyOriginOption,
  FieldValuesSetter,
} from 'features/libra/ticket/fieldOrigin'
import { toPersistedShape } from 'features/libra/ticket/fieldValuesPatch'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useActiveStepId } from 'features/libra/ticket/TicketStateContext'
import {
  FieldAccessScopePath,
  useActiveScopedFieldAccess,
} from 'features/libra/ticket/useScopedFieldAccess'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { useComponentScope } from 'features/libra/workspace/ComponentScopeContext'
import isEqual from 'lodash/isEqual'
import omitBy from 'lodash/omitBy'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { deriveStepFieldValues } from './deriveStepFieldValues'

type UnsavedFieldEdit = { wrapper: FieldValue | undefined; revision: number }
const noUnsavedEdits: Record<string, UnsavedFieldEdit> = {}

const withUnsavedEdits = (
  storedFieldValues: FieldValues,
  unsavedEdits: Record<string, UnsavedFieldEdit>
): FieldValues => {
  const merged: FieldValues = { ...storedFieldValues }
  for (const [key, { wrapper }] of Object.entries(unsavedEdits)) {
    if (wrapper === undefined) delete merged[key]
    else merged[key] = wrapper
  }
  return merged
}

const persistedWrapper = (key: string, wrapper: FieldValue | undefined) =>
  toPersistedShape(wrapper === undefined ? {} : { [key]: wrapper })[key]

const matchesStoredValue = (
  storedFieldValues: FieldValues,
  key: string,
  wrapper: FieldValue | undefined
) =>
  isEqual(
    persistedWrapper(key, storedFieldValues[key]),
    persistedWrapper(key, wrapper)
  )

type UseWorkflowFieldValuesOptions = {
  ontologyFields: Record<string, OntologyField>
  sections: ReadonlyArray<Section>
  form: ReadonlyArray<FormLayoutItem>
}

type SaveOwner = {
  ticketId: string
  stepId: string
  scope: FieldAccessScopePath
  generation: number
}

const sameOwner = (left: SaveOwner, right: SaveOwner): boolean =>
  left.ticketId === right.ticketId &&
  left.stepId === right.stepId &&
  left.generation === right.generation &&
  left.scope.kind === right.scope.kind &&
  (left.scope.kind === 'ticket' ||
    (right.scope.kind === 'component' &&
      left.scope.componentId === right.scope.componentId))

export const useWorkflowFieldValues = ({
  ontologyFields,
  sections,
  form,
}: UseWorkflowFieldValuesOptions) => {
  const activeStepId = useActiveStepId()
  const workflow = useWorkflow()
  const access = useActiveScopedFieldAccess()
  const ticketAccess = useTicketAccess()
  const componentScope = useComponentScope()
  const componentId = componentScope?.componentId
  const { enqueueFieldValues, getCommittedFieldValues, isLoading } = access
  const scope = useMemo<FieldAccessScopePath>(
    () =>
      componentId !== undefined
        ? { kind: 'component', componentId }
        : { kind: 'ticket' },
    [componentId]
  )
  const ownerIdentity = {
    ticketId: ticketAccess.ticketId,
    stepId: activeStepId,
    scope,
  }
  const ownerRef = useRef<SaveOwner | undefined>(undefined)
  const previousOwner = ownerRef.current
  let owner = previousOwner
  if (
    !owner ||
    owner.ticketId !== ownerIdentity.ticketId ||
    owner.stepId !== ownerIdentity.stepId ||
    owner.scope.kind !== scope.kind ||
    (scope.kind === 'component' &&
      (owner.scope.kind !== 'component' ||
        owner.scope.componentId !== scope.componentId))
  ) {
    owner = {
      ...ownerIdentity,
      generation: (previousOwner?.generation ?? -1) + 1,
    }
    ownerRef.current = owner
  }
  const branch = useMemo(
    () => workflow.steps.find((step) => step.id === activeStepId)?.branch,
    [activeStepId, workflow.steps]
  )

  const initialFieldValues = useMemo<FieldValues>(
    () => FieldValuesSchema.parse(access.committedFieldValues ?? {}),
    [access.committedFieldValues]
  )
  const [editState, setEditState] = useState<{
    owner: SaveOwner
    edits: Record<string, UnsavedFieldEdit>
  }>({ owner, edits: {} })
  const localEdits = sameOwner(editState.owner, owner)
    ? editState.edits
    : noUnsavedEdits
  const localEditsRef = useRef(localEdits)
  const editRevisionRef = useRef(0)
  const storedFieldValuesRef = useRef(initialFieldValues)
  storedFieldValuesRef.current = initialFieldValues

  const saveContextRef = useRef({ owner, readOnly: access.readOnly })
  saveContextRef.current = { owner, readOnly: access.readOnly }
  const savingRef = useRef(false)
  const mountedRef = useRef(true)
  const [drawingResetKey, setDrawingResetKey] = useState(0)
  const [isSaving, setIsSaving] = useState(false)

  if (!sameOwner(editState.owner, owner)) {
    localEditsRef.current = {}
    setEditState({ owner, edits: {} })
  }

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const fieldValuesWithUnsavedEdits = useMemo(
    () => withUnsavedEdits(initialFieldValues, localEdits),
    [initialFieldValues, localEdits]
  )

  const getSavedFieldValues = useCallback(
    () => FieldValuesSchema.parse(getCommittedFieldValues() ?? {}),
    [getCommittedFieldValues]
  )

  const getCurrentFieldValues = useCallback(
    () => withUnsavedEdits(storedFieldValuesRef.current, localEditsRef.current),
    []
  )

  const canWrite = useCallback((submittedOwner: SaveOwner) => {
    const current = saveContextRef.current
    return (
      mountedRef.current &&
      !current.readOnly &&
      sameOwner(current.owner, submittedOwner)
    )
  }, [])

  const replaceLocalEdits = useCallback(
    (expectedOwner: SaveOwner, edits: Record<string, UnsavedFieldEdit>) => {
      if (!sameOwner(saveContextRef.current.owner, expectedOwner)) return false
      localEditsRef.current = edits
      setEditState({ owner: expectedOwner, edits })
      return true
    },
    []
  )

  const setFieldValues = useCallback<FieldValuesSetter>(
    (action, option) => {
      if (!sameOwner(saveContextRef.current.owner, owner)) return
      const current = getCurrentFieldValues()
      const next = applyOriginOption(
        typeof action === 'function' ? action(current) : action,
        option
      )
      const nextEdits: Record<string, UnsavedFieldEdit> = {
        ...localEditsRef.current,
      }
      let changed = false
      for (const key of new Set([
        ...Object.keys(current),
        ...Object.keys(next),
      ])) {
        if (current[key] === next[key] || isEqual(current[key], next[key]))
          continue
        changed = true
        if (
          !savingRef.current &&
          matchesStoredValue(storedFieldValuesRef.current, key, next[key])
        ) {
          delete nextEdits[key]
        } else {
          nextEdits[key] = {
            wrapper: next[key],
            revision: ++editRevisionRef.current,
          }
        }
      }
      if (changed) replaceLocalEdits(owner, nextEdits)
    },
    [getCurrentFieldValues, owner, replaceLocalEdits]
  )

  const { allVisibleSections, resolvedFieldValues, fieldValuesToSave } =
    useMemo(
      () =>
        deriveStepFieldValues({
          fieldValues: fieldValuesWithUnsavedEdits,
          sections,
          form,
          ontologyFields,
          branch,
        }),
      [fieldValuesWithUnsavedEdits, sections, form, ontologyFields, branch]
    )

  const hasUnsavedEditsOrSystemValues = useMemo(
    () =>
      createPatchData(
        'id',
        toPersistedShape(initialFieldValues),
        toPersistedShape(fieldValuesToSave)
      ) !== undefined,
    [fieldValuesToSave, initialFieldValues]
  )

  const hasUnsavedEdits = useMemo(
    () =>
      createPatchData(
        'id',
        toPersistedShape(initialFieldValues),
        toPersistedShape(fieldValuesWithUnsavedEdits)
      ) !== undefined,
    [fieldValuesWithUnsavedEdits, initialFieldValues]
  )

  const isSavingNow = useCallback(() => savingRef.current, [])

  const canProceed = useCallback(() => {
    if (savingRef.current) return false
    const currentFieldValuesToSave = deriveStepFieldValues({
      fieldValues: getCurrentFieldValues(),
      sections,
      form,
      ontologyFields,
      branch,
    }).fieldValuesToSave
    return (
      createPatchData(
        'id',
        toPersistedShape(getSavedFieldValues()),
        toPersistedShape(currentFieldValuesToSave)
      ) === undefined
    )
  }, [
    getCurrentFieldValues,
    getSavedFieldValues,
    sections,
    form,
    ontologyFields,
    branch,
  ])

  const startSave = useCallback(
    (expectedOwner: SaveOwner) => {
      if (!canWrite(expectedOwner))
        throw new Error('The form save owner changed')
      if (savingRef.current)
        throw new Error('A form save is already in progress')
      savingRef.current = true
      setIsSaving(true)
    },
    [canWrite]
  )

  const finishSave = useCallback(() => {
    savingRef.current = false
    if (mountedRef.current) setIsSaving(false)
  }, [])

  const handleCancel = useCallback(() => {
    if (savingRef.current || !sameOwner(saveContextRef.current.owner, owner))
      return
    replaceLocalEdits(owner, {})
    setDrawingResetKey((current) => current + 1)
  }, [owner, replaceLocalEdits])

  const handleSave = useCallback(async () => {
    startSave(owner)
    const submittedEdits = localEditsRef.current
    try {
      const submittedFieldValues = deriveStepFieldValues({
        fieldValues: getCurrentFieldValues(),
        sections,
        form,
        ontologyFields,
        branch,
      }).fieldValuesToSave
      const appliedValues = await enqueueFieldValues((_, ticketId) =>
        canWrite(owner) && ticketId === owner.ticketId
          ? submittedFieldValues
          : null
      )
      if (!appliedValues || !canWrite(owner)) {
        throw new Error('The form save was not applied')
      }
      for (const fieldId of Object.keys(submittedFieldValues)) {
        const value = appliedValues[fieldId]
        if (
          !Object.hasOwn(appliedValues, fieldId) ||
          !value ||
          !FieldValueSchema.safeParse(value).success
        ) {
          throw new Error('The form save acknowledgement was invalid')
        }
      }
      for (const value of Object.values(appliedValues)) {
        if (!FieldValueSchema.safeParse(value).success) {
          throw new Error('The form save acknowledgement was invalid')
        }
      }
      const savedAfterWrite = getSavedFieldValues()
      storedFieldValuesRef.current = savedAfterWrite
      const remainingEdits = omitBy(
        localEditsRef.current,
        (edit, key) =>
          submittedEdits[key]?.revision === edit.revision ||
          matchesStoredValue(savedAfterWrite, key, edit.wrapper)
      )
      replaceLocalEdits(owner, remainingEdits)
    } finally {
      finishSave()
    }
  }, [
    branch,
    canWrite,
    getCurrentFieldValues,
    enqueueFieldValues,
    finishSave,
    form,
    getSavedFieldValues,
    ontologyFields,
    owner,
    replaceLocalEdits,
    sections,
    startSave,
  ])

  const savePartialField = useCallback<SavePartialField>(
    async (fieldId, prepare, reconcile) => {
      startSave(owner)
      try {
        const appliedFields = await enqueueFieldValues(
          (latestFields, ticketId) => {
            if (!canWrite(owner) || ticketId !== owner.ticketId) return null
            const value = prepare(latestFields[fieldId])
            return FieldValueSchema.safeParse(value).success
              ? { [fieldId]: value }
              : null
          }
        )
        if (!appliedFields || !canWrite(owner)) {
          throw new Error('The partial field save was not applied')
        }
        const value = appliedFields[fieldId]
        if (!value || !FieldValueSchema.safeParse(value).success) {
          throw new Error('The partial field save acknowledgement was invalid')
        }
        for (const acknowledgedValue of Object.values(appliedFields)) {
          if (!FieldValueSchema.safeParse(acknowledgedValue).success) {
            throw new Error(
              'The partial field save acknowledgement was invalid'
            )
          }
        }
        const savedAfterWrite = getSavedFieldValues()
        storedFieldValuesRef.current = savedAfterWrite
        const current = withUnsavedEdits(
          savedAfterWrite,
          localEditsRef.current
        )[fieldId]
        const reconciled = reconcile(current, value)
        const remainingEdits = omitBy(
          localEditsRef.current,
          (edit, key) =>
            key === fieldId ||
            matchesStoredValue(savedAfterWrite, key, edit.wrapper)
        )
        if (!matchesStoredValue(savedAfterWrite, fieldId, reconciled)) {
          remainingEdits[fieldId] = {
            wrapper: reconciled,
            revision: ++editRevisionRef.current,
          }
        }
        replaceLocalEdits(owner, remainingEdits)
      } finally {
        finishSave()
      }
    },
    [
      canWrite,
      enqueueFieldValues,
      finishSave,
      getSavedFieldValues,
      owner,
      replaceLocalEdits,
      startSave,
    ]
  )

  return {
    fieldValues: fieldValuesWithUnsavedEdits,
    resolvedFieldValues,
    initialFieldValues,
    drawingResetKey,
    setFieldValues,
    allVisibleSections,
    hasUnsavedEditsOrSystemValues: hasUnsavedEditsOrSystemValues || isSaving,
    hasUnsavedEdits: hasUnsavedEdits || isSaving,
    handleCancel,
    handleSave,
    savePartialField,
    canProceed,
    isSavingNow,
    isSaving,
    isLoading,
  }
}

export type UseWorkflowFieldValuesReturn = ReturnType<
  typeof useWorkflowFieldValues
>
